import React, { useState } from 'react'
import { Star } from 'lucide-react'
import { clsx } from 'clsx'

interface StarRatingProps {
  value: number
  onChange?: (rating: number) => void
  size?: 'sm' | 'md' | 'lg'
  readOnly?: boolean
}

const starSizes = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8'
}

const StarRating: React.FC<StarRatingProps> = ({ value, onChange, size = 'md', readOnly = false }) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null)

  const displayValue = hoverValue ?? value
  const isInteractive = !readOnly && !!onChange

  return (
    <div
      className="inline-flex items-center gap-0.5"
      onMouseLeave={() => isInteractive && setHoverValue(null)}
      role={isInteractive ? 'radiogroup' : undefined}
      aria-label={isInteractive ? 'Star rating' : `Rated ${value} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!isInteractive}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => isInteractive && setHoverValue(star)}
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
          className={clsx(
            'disabled:cursor-default',
            isInteractive && 'cursor-pointer'
          )}
        >
          <Star
            className={clsx(
              starSizes[size],
              Math.round(displayValue) >= star ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-gray-300'
            )}
          />
        </button>
      ))}
    </div>
  )
}

export default StarRating
